import { getAddress } from "viem";
import { z } from "zod";
export const INDEXER_MAX_BLOCK_BATCH = 50;
export const INDEXER_LAG_SLO_BLOCKS = 50;
export const INDEXER_CURSOR_AGE_SLO_SECONDS = 30;
export const ethereumAddressSchema = z.string().transform((value, context) => {
    try {
        return getAddress(value);
    }
    catch {
        context.addIssue({
            code: "custom",
            message: "Invalid Ethereum address or mixed-case checksum",
        });
        return z.NEVER;
    }
});
const optionalDeploymentAddressSchema = ethereumAddressSchema.nullable();
const absoluteHttpUrlSchema = z
    .string()
    .url()
    .refine((value) => value.startsWith("http://") || value.startsWith("https://"), {
    message: "Expected an absolute HTTP(S) URL",
});
export const protocolAddressesSchema = z
    .object({
    factory: optionalDeploymentAddressSchema,
    marketplace: optionalDeploymentAddressSchema,
    safe: optionalDeploymentAddressSchema,
    treasury: optionalDeploymentAddressSchema,
})
    .readonly();
export const pancakeV2AddressesSchema = z
    .object({
    factory: optionalDeploymentAddressSchema,
    wbnb: optionalDeploymentAddressSchema,
})
    .readonly();
export const networkConfigSchema = z
    .object({
    chainId: z.number().int().positive().safe(),
    name: z.string().min(1),
    protocol: protocolAddressesSchema,
    deploymentBlock: z.bigint().nonnegative().nullable(),
    pancakeV2: pancakeV2AddressesSchema,
    rpcHttpUrls: z.array(absoluteHttpUrlSchema).min(1).readonly(),
    publicOrigins: z.array(absoluteHttpUrlSchema).readonly(),
    indexerMaxBlockBatch: z.literal(INDEXER_MAX_BLOCK_BATCH),
    indexerLagSloBlocks: z.literal(INDEXER_LAG_SLO_BLOCKS),
    indexerCursorAgeSloSeconds: z.literal(INDEXER_CURSOR_AGE_SLO_SECONDS),
})
    .superRefine((network, context) => {
    const protocolAddresses = Object.values(network.protocol);
    const hasAnyProtocolAddress = protocolAddresses.some((address) => address !== null);
    const hasAllProtocolAddresses = protocolAddresses.every((address) => address !== null);
    if (hasAnyProtocolAddress !== hasAllProtocolAddresses) {
        context.addIssue({
            code: "custom",
            path: ["protocol"],
            message: "Protocol addresses must be either entirely absent or entirely populated",
        });
    }
    if (hasAllProtocolAddresses !== (network.deploymentBlock !== null)) {
        context.addIssue({
            code: "custom",
            path: ["deploymentBlock"],
            message: "deploymentBlock and the complete protocol address set must be configured together",
        });
    }
    if (network.chainId === 56 || network.chainId === 97) {
        if (network.pancakeV2.factory === null || network.pancakeV2.wbnb === null) {
            context.addIssue({
                code: "custom",
                path: ["pancakeV2"],
                message: "BSC networks require official PancakeSwap V2 Factory and WBNB addresses",
            });
        }
    }
})
    .readonly();
export function assertDeployedNetwork(network) {
    if (network.deploymentBlock === null ||
        network.protocol.factory === null ||
        network.protocol.marketplace === null ||
        network.protocol.safe === null ||
        network.protocol.treasury === null) {
        throw new Error(`TapeOut V3 is not deployed on chain ${network.chainId}`);
    }
}
//# sourceMappingURL=schema.js.map